import React from "react";

/**
 * Button component - rounded-lg (not fully rounded), primary/secondary variants.
 * Follows UI guidelines for professional, consistent styling.
 * Supports `as` prop to render as Link (e.g. as={Link} to="/path").
 * @see .cursor/ui.md
 */
const Button = ({
  children,
  variant = "primary", // primary | secondary | outline | ghost | danger
  size = "default", // sm | default | lg
  type = "button",
  disabled = false,
  loading = false,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  className = "",
  fullWidth = false,
  as: Component = "button",
  ...props
}) => {
  const baseClasses =
    "inline-flex items-center justify-center gap-2 font-medium transition-colors duration-200 ease-out rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

  const variantClasses = {
    primary:
      "bg-primary text-white hover:bg-primary-dark focus:ring-primary/30",
    secondary:
      "bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-300",
    outline:
      "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:ring-primary/20 focus:border-primary",
    ghost: "bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-200",
    danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500/30",
    "secondary-primary":
      "bg-primary/10 text-primary hover:bg-primary/20 focus:ring-primary/20",
  };

  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm",
    default: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base",
  };

  const buttonClasses = [
    baseClasses,
    variantClasses[variant] || variantClasses.primary,
    sizeClasses[size] || sizeClasses.default,
    fullWidth && "w-full",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const isButton = Component === "button";
  const finalClassName = [
    buttonClasses,
    !isButton && (disabled || loading) && "pointer-events-none opacity-50",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Component
      className={finalClassName}
      type={isButton ? type : undefined}
      disabled={isButton ? disabled || loading : undefined}
      {...props}
    >
      {loading ? (
        <>
          <svg
            className="animate-spin h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>Loading...</span>
        </>
      ) : (
        <>
          {LeftIcon && <LeftIcon className="w-5 h-5 shrink-0" />}
          {children}
          {RightIcon && <RightIcon className="w-5 h-5 shrink-0" />}
        </>
      )}
    </Component>
  );
};

export default Button;
